import React, { useEffect, useMemo, useRef, useState } from "react"
import { ChevronDownIcon, SearchIcon } from "@heroicons/react/outline"

import { getMonthlyPaidAdPriceForTopic } from "../../utils/topicAdPrice"

/**
 * @param {{
 *   topics: Array<{ slug: string, name?: string | null, views?: string | number | null }>,
 *   selectedSlug: string | null,
 *   selectedRowPriceUsd: number | null,
 *   onSelect: (slug: string) => void,
 * }} props
 */
const PaidAdTopicPicker = ({ topics, selectedSlug, selectedRowPriceUsd, onSelect }) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const rootRef = useRef(null)

  useEffect(() => {
    if (!open) {
      return
    }
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", onDoc)
    return () => document.removeEventListener("mousedown", onDoc)
  }, [open])

  useEffect(() => {
    if (!open) {
      return
    }
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false)
      }
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [open])

  const sorted = useMemo(() => {
    return [...topics].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" })
    )
  }, [topics])

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) {
      return sorted
    }
    return sorted.filter(
      (t) =>
        String(t.name || "")
          .toLowerCase()
          .includes(needle) ||
        String(t.slug || "")
          .toLowerCase()
          .includes(needle)
    )
  }, [sorted, query])

  const selectedTopic = useMemo(
    () => (selectedSlug ? topics.find((t) => t.slug === selectedSlug) : null),
    [topics, selectedSlug]
  )

  const triggerLabel =
    selectedTopic == null
      ? null
      : `${selectedTopic.name} · $${selectedRowPriceUsd ?? getMonthlyPaidAdPriceForTopic(selectedTopic)}/mo`

  const handlePick = (slug) => {
    onSelect(slug)
    setOpen(false)
    setQuery("")
  }

  return (
    <div ref={rootRef} className="relative mt-5 text-left">
      <div className="rounded-lg bg-white p-2 shadow ring-1 ring-gray-200">
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="flex w-full items-center justify-between gap-2 rounded-md border-0 bg-transparent px-2 py-2.5 text-left text-sm font-medium text-gray-900 transition hover:bg-gray-50/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/40"
        >
          <span className={triggerLabel ? "min-w-0 truncate text-gray-900" : "min-w-0 truncate text-gray-500"}>
            {triggerLabel ?? "Select topic page"}
          </span>
          <ChevronDownIcon
            className={`h-5 w-5 flex-shrink-0 text-gray-500 transition ${open ? "-rotate-180" : ""}`}
            aria-hidden
          />
        </button>
      </div>

      {open ? (
        <div
          className="absolute left-0 right-0 z-50 mt-2 rounded-lg border border-gray-200 bg-white py-1 shadow-lg ring-1 ring-gray-200/80"
          role="listbox"
          aria-label="Topic pages"
        >
          <label className="sr-only" htmlFor="paid-ad-topic-search">
            Search topics
          </label>
          <div className="border-b border-gray-100 px-3 pb-2 pt-2">
            <div className="relative">
              <SearchIcon
                className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400"
                aria-hidden
              />
              <input
                id="paid-ad-topic-search"
                type="search"
                autoComplete="off"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search"
                className="w-full rounded-md border border-gray-200 bg-white py-2 pl-9 pr-2 text-sm text-gray-800 placeholder:text-gray-400 focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500/30"
              />
            </div>
          </div>
          <ul className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-3 text-center text-sm text-gray-500">No match.</li>
            ) : (
              filtered.map((t) => {
                const selected = t.slug === selectedSlug
                const price = getMonthlyPaidAdPriceForTopic(t)
                return (
                  <li key={t.slug} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => handlePick(t.slug)}
                      className={`flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm ${
                        selected ? "bg-rose-50 text-rose-900" : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <span className="min-w-0 truncate font-medium">{t.name}</span>
                      <span className="flex-shrink-0 tabular-nums text-xs font-semibold text-gray-500">
                        ${price}/mo
                      </span>
                    </button>
                  </li>
                )
              })
            )}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

export default PaidAdTopicPicker
